import { Circle, Flex, Icon, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { LuClock } from 'react-icons/lu';
import { useI18n } from 'src/i18n/I18nContext';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { OrgUtil } from 'src/data/OrgUtil';
import type { Role } from 'src/api/Role';
import { useTimerContext, type TimerStopData } from './TimerContext';
import TimerStopDialog from './TimerStopDialog';
import { MemberInhabitant } from '@/api/Member';

const formatElapsed = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

interface TimerButtonProps {
  variant: 'desktop' | 'mobile';
  role?: Role;
}

const DesktopTimerButton = ({ isRunning, elapsedSeconds, onToggle, t }: {
  isRunning: boolean;
  elapsedSeconds: number;
  onToggle: () => void;
  t: (key: string) => string;
}) => (
  <Tooltip content={isRunning ? t('header.timer.stop') : t('header.timer.start')}>
    <Button
      variant='ghost'
      height='11'
      rounded='full'
      px='3'
      gap='2'
      minW='0'
      borderWidth='1px'
      borderColor={isRunning ? 'green.border' : 'border.subtle'}
      color={isRunning ? 'green.fg' : 'fg'}
      bg={isRunning ? 'green.subtle' : 'bg.panel'}
      boxShadow='md'
      _hover={{
        bg: isRunning ? 'green.muted' : 'bg.subtle',
      }}
      onClick={onToggle}
    >
      {isRunning ? (
        <>
          <Circle size='2' bg='red.solid' animation='pulse 2s infinite' />
          <Text fontFamily='mono' fontSize='sm' fontWeight='semibold' minW='5.5ch'>
            {formatElapsed(elapsedSeconds)}
          </Text>
        </>
      ) : (
        <Icon boxSize='5' as={LuClock} />
      )}
    </Button>
  </Tooltip>
);

const MobileTimerButton = ({ isRunning, elapsedSeconds, onToggle }: {
  isRunning: boolean;
  elapsedSeconds: number;
  onToggle: () => void;
}) => (
  <Button
    variant='ghost'
    boxSize='10'
    minW='10'
    rounded='lg'
    p='0'
    borderWidth='1px'
    boxShadow='md'
    borderColor={isRunning ? 'red.border' : 'border.subtle'}
    color={isRunning ? 'red.fg' : 'fg'}
    bg={isRunning ? 'red.subtle' : 'bg.panel'}
    _hover={{
      bg: isRunning ? 'red.muted' : 'bg.subtle',
    }}
    onClick={onToggle}
    aria-label={isRunning ? formatElapsed(elapsedSeconds) : 'Timer'}
  >
    {isRunning ? (
      <Flex align='center' gap='1'>
        <Circle size='1.5' bg='red.solid' />
        <Text fontFamily='mono' fontSize='xs' fontWeight='semibold'>
          {formatElapsed(elapsedSeconds)}
        </Text>
      </Flex>
    ) : (
      <Icon boxSize='5' as={LuClock} />
    )}
  </Button>
);

const TimerButton = ({ variant, role }: TimerButtonProps) => {
  const { t } = useI18n();
  const { isRunning, elapsedSeconds, startTimer, stopTimer } = useTimerContext();
  const [timerData, setTimerData] = useState<TimerStopData | null>(null);

  if (!OrgUtil.isAllowed(role, MemberInhabitant)) return null;

  const handleToggle = () => {
    if (isRunning) {
      const data = stopTimer();
      if (data) {
        setTimerData(data);
      }
    } else {
      startTimer();
    }
  };

  const handleDialogClose = () => {
    setTimerData(null);
  };

  return (
    <>
      {variant === 'desktop' ? (
        <DesktopTimerButton
          isRunning={isRunning}
          elapsedSeconds={elapsedSeconds}
          onToggle={handleToggle}
          t={t}
        />
      ) : (
        <MobileTimerButton
          isRunning={isRunning}
          elapsedSeconds={elapsedSeconds}
          onToggle={handleToggle}
        />
      )}
      {timerData && (
        <TimerStopDialog
          timerData={timerData}
          open={!!timerData}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
};

export default TimerButton;
